import { ILoadingObject } from "./ILoadingObject";
import { LoaderObject } from "./LoaderObject";
import { LoadingQueueEndElement } from "./LoadingQueueEndElement";
import { Loader } from "@goodgamestudios/createjs-utilities";
import { URLLoader, URLRequest } from "@goodgamestudios/createjs-utilities";
import { LoaderInfo } from "@goodgamestudios/createjs-utilities";

import Event = createjs.Event;
import EventDispatcher = createjs.EventDispatcher;
import IOErrorEvent = createjs.IOErrorEvent;
import ProgressEvent = createjs.ProgressEvent;
import SecurityErrorEvent = createjs.SecurityErrorEvent;

import { Signal } from "signals.js";

/**
 * <p>GoodgameLoader is a simple manager for a loading queue. You can add different kinds of loader and read out current loading status or status of every
 * single loader inside. This object uses the AS3-Signal Framework for a lightweight event-handling. More information about AS3-Signals
 * can be found at https://github.com/robertpenner/as3-signals. The queue will be started automatically
 * if you add one element.</p>
 *
 *
 * @author dkoehler
 */
export class GoodgameLoader {
    /** unique id for this GoodgameLoader. */
    private _loaderID: string;

    /** unique id for this GoodgameLoader. */
    public get loaderID(): string {
        return this._loaderID;
    }

    /** flag if queue is loading. */
    private _isLoading: boolean = false;

    /** flag if queue is loading. */
    public get isLoading(): boolean {
        return this._isLoading;
    }

    private _loadingError: Signal = new Signal();

    public get loadingError(): Signal {
        return this._loadingError;
    }

    /** signal that an element of actual loading queue is finished. name of this loader and subloader will be a parameter */
    private _loadingQueueElementFinished: Signal = new Signal();

    /** signal that an element of actual loading queue is finished. name of this loader and subloader will be a parameter */
    public get loadingQueueElementFinished(): Signal {
        return this._loadingQueueElementFinished;
    }

    /** signal that loading process of an element was started. name of this loader and subloader will be a parameter */
    private _loadingQueueElementStarted: Signal = new Signal();

    /** signal that loading process of an element was started. name of this loader and subloader will be a parameter */
    public get loadingQueueElementStarted(): Signal {
        return this._loadingQueueElementStarted;
    }

    /** contains all active loader. */
    private _loaderMap: Map<any, any> = new Map<any, any>();

    /** loading queue. */
    private _queue: ILoadingObject[] = [];

    /** list with all completed queue end elements. */
    private _finishedQueueEndElements: any[] = new Array();

    /** start time of loader. */
    private _loadingStartTime: number;

    /** flag if errors of subloader should be thrown. */
    private subloaderErrorLogging: boolean = true;

    /**
     * Constructor
     *
     * @param id unique id for this GoodgameLoader
     * @param waitTime time how long queue should wait for new elements after processing the last element before loadingQueueFinished will be send
     * @param errorLogging flag if errors of subloader should be thrown
     */
    constructor(id: string, errorLoggingActive: boolean = true) {
        this._loaderID = id;
        this.subloaderErrorLogging = errorLoggingActive;
    }

    /**
     * adding a swf to load. loading begins automaticly
     *
     * @return created or cached LoaderObject
     *
     * @param loaderName name for this loading element
     * @param url url of file to load. if no url is set, request must be set
     * @param request URLRequest for this SWF-Loader. if no request is set, url must be set
     * @param context not used - kept for signature backward compatibility
     * @param onCompleteCallback callback that should be executed after finished loading
     * @param overwrite flag if an existing loader with same id should be overwritten
     */
    public addCookieSaverLoader(loaderName: string, url: string = "", request: URLRequest = null, context: any = null, onCompleteCallback: Function = null, overwrite: boolean = false, isItCookie: boolean = false): LoaderObject {
        if (!this._loaderMap.get(loaderName) || overwrite == true) {
            if (this._loaderMap.get(loaderName) && overwrite == true) {
                let oldLoader: LoaderObject = this._loaderMap.get(loaderName);
                let concreteLoader: Loader = (<Loader>oldLoader.loaderInstance);
                concreteLoader.contentLoaderInfo.removeEventListener(IOErrorEvent.IO_ERROR, oldLoader.bindFunction(oldLoader.handleLoaderIOError));
                concreteLoader.contentLoaderInfo.removeEventListener(SecurityErrorEvent.SECURITY_ERROR, oldLoader.bindFunction(oldLoader.handleLoaderSecurityError));
                concreteLoader.contentLoaderInfo.removeEventListener(ProgressEvent.PROGRESS, oldLoader.bindFunction(oldLoader.onLoadProgress));
                concreteLoader.contentLoaderInfo.removeEventListener(Event.COMPLETE, oldLoader.bindFunction(oldLoader.onComplete));
                oldLoader.dispose();
            }

            if(!url)  {
                return null;
            }
            request = request || new URLRequest(url);

            let _loader: Loader = new Loader();
            let loaderObject: LoaderObject = new LoaderObject(_loader, loaderName, request,  onCompleteCallback);
            loaderObject.loaderObjectFinished.add(this.bindFunction(this.queueElementHasFinished));
            loaderObject.loadingProgressStarted.add(this.bindFunction(this.queueElementNowLoading));
            loaderObject.loadingError.add(this.loadingError.bindFunction(this.loadingError.dispatch));
            _loader.contentLoaderInfo.addEventListener(IOErrorEvent.IO_ERROR, loaderObject.bindFunction(loaderObject.handleLoaderIOError));
            _loader.contentLoaderInfo.addEventListener(SecurityErrorEvent.SECURITY_ERROR, loaderObject.bindFunction(loaderObject.handleLoaderSecurityError));
            _loader.contentLoaderInfo.addEventListener(ProgressEvent.PROGRESS, loaderObject.bindFunction(loaderObject.onLoadProgress));
            _loader.contentLoaderInfo.addEventListener(Event.COMPLETE, loaderObject.bindFunction(loaderObject.onComplete));

            this._loaderMap.set(loaderName, loaderObject);
            this._queue.push(loaderObject);
            this.startLoading();
            return loaderObject;
        }
        else {
            return this._loaderMap.get(loaderName);
        }

    }

    /**
     * adding a xml to load. loading begins automatically
     *
     * @param loaderName name for this loading element
     * @param url url of file
     * @param dataFormat data format that will be expected (flash.net.URLLoaderDataFormat)
     * @param onCompleteCallback callback that should be executed after finished loading
     * @param overwrite flag if an existing loader with same id should be overwritten
     */
    public addXMLLoader(loaderName: string, url: string, dataFormat: string, onCompleteCallback: Function = null, overwrite: boolean = false): void {
        if (!this._loaderMap.get(loaderName) || overwrite == true) {
            if (this._loaderMap.get(loaderName) && overwrite == true) {
                let oldLoader: LoaderObject = this._loaderMap.get(loaderName);
                let concreteLoader: createjs.LoadQueue = (<createjs.LoadQueue>oldLoader.loaderInstance);
                // concreteLoader.removeEventListener(IOErrorEvent.IO_ERROR, oldLoader.bindFunction(oldLoader.handleLoaderIOError));
                // concreteLoader.removeEventListener(SecurityErrorEvent.SECURITY_ERROR, oldLoader.bindFunction(oldLoader.handleLoaderSecurityError));
                // concreteLoader.removeEventListener(ProgressEvent.PROGRESS, oldLoader.bindFunction(oldLoader.onLoadProgress));
                // concreteLoader.removeEventListener(Event.COMPLETE, oldLoader.bindFunction(oldLoader.onComplete));
                concreteLoader.removeEventListener("complete", oldLoader.bindFunction(oldLoader.onComplete));
                concreteLoader.removeEventListener("progress", oldLoader.bindFunction(oldLoader.onLoadProgress));
                concreteLoader.removeEventListener("error", oldLoader.bindFunction(oldLoader.handleLoaderIOError));
                oldLoader.dispose();
            }
            let request: URLRequest = new URLRequest(url);

            let _loadItem: createjs.LoadItem = new createjs.LoadItem();
            _loadItem.id = loaderName;
            _loadItem.src = url;
            _loadItem.type = dataFormat;

            let _loader = new createjs.LoadQueue();

            let loaderObject: LoaderObject = new LoaderObject(_loader, loaderName, request,  onCompleteCallback);
            loaderObject.loaderObjectFinished.add(this.bindFunction(this.queueElementHasFinished));
            loaderObject.loadingProgressStarted.add(this.bindFunction(this.queueElementNowLoading));
            loaderObject.loadingError.add(this.loadingError.bindFunction(this.loadingError.dispatch));

            _loader.addEventListener("complete", loaderObject.bindFunction(loaderObject.onComplete));
            _loader.addEventListener("progress", loaderObject.bindFunction(loaderObject.onLoadProgress));
            _loader.addEventListener("error", loaderObject.bindFunction(loaderObject.handleLoaderIOError));

           /* _loader.addEventListener(IOErrorEvent.IO_ERROR, loaderObject.bindFunction(loaderObject.handleLoaderIOError));
            _loader.addEventListener(SecurityErrorEvent.SECURITY_ERROR, loaderObject.bindFunction(loaderObject.handleLoaderSecurityError));
            _loader.addEventListener(ProgressEvent.PROGRESS, loaderObject.bindFunction(loaderObject.onLoadProgress));
            _loader.addEventListener(Event.COMPLETE, loaderObject.bindFunction(loaderObject.onComplete));*/

            _loader.loadFile( _loadItem, true );

            this._loaderMap.set(loaderName, loaderObject);
            this._queue.push(loaderObject);
            this.startLoading();
            // TODO: @HTML5 - need a new logging system
            //LogChannels.loading.debug("XMLLoader added: " + loaderName);
        }
        else {
            // TODO: @HTML5 - need a new logging system
            //LogChannels.loading.fatal("loader with name: " + loaderName + " already existing");
        }
    }

    /**
     * adding a LoaderInfo-Object to queue and observe it.
     *
     * @param loaderName name for this loading element
     * @param loaderInfo reference of LoaderInfo
     * @param onCompleteCallback callback that should be executed after finished loading
     */
    public addLoaderInfoObject(loaderName: string, loaderInfo: LoaderInfo, onCompleteCallback: Function = null): void {
        if (!this._loaderMap.get(loaderName)) {
            let loaderObject: LoaderObject = new LoaderObject(loaderInfo, loaderName, null,  onCompleteCallback);
            loaderObject.loaderObjectFinished.add(this.bindFunction(this.queueElementHasFinished));
            loaderObject.loadingProgressStarted.add(this.bindFunction(this.queueElementNowLoading));
            loaderObject.loadingError.add(this.loadingError.bindFunction(this.loadingError.dispatch));
            loaderInfo.addEventListener(ProgressEvent.PROGRESS, loaderObject.bindFunction(loaderObject.onLoadProgress));
            loaderInfo.addEventListener(Event.COMPLETE, loaderObject.bindFunction(loaderObject.onComplete));
            this._loaderMap.set(loaderName, loaderObject);
            this._queue.push(loaderObject);
            this.startLoading();
            // TODO: @HTML5 - need a new logging system
            //LogChannels.loading.debug("LoaderInfo-Object added: " + loaderName);
        }
        else {
            // TODO: @HTML5 - need a new logging system
            //LogChannels.loading.fatal("LoaderInfo-Object with name: " + loaderName + " already existing");
        }
    }

    /**
     * adds an URLRequest to loading queue
     *
     * @param loaderName unique name of this loader
     * @param req request that should be loaded
     * @return
     *
     */
    public addURLRequest(loaderName: string, req: URLRequest, onCompleteCallback: Function = null): void {
        if (!this._loaderMap.get(loaderName)) {
            let _loader: URLLoader = new URLLoader();
            let loaderObject: LoaderObject = new LoaderObject(_loader, loaderName, req,  onCompleteCallback);
            loaderObject.loaderObjectFinished.add(this.bindFunction(this.queueElementHasFinished));
            loaderObject.loadingProgressStarted.add(this.bindFunction(this.queueElementNowLoading));
            loaderObject.loadingError.add(this.loadingError.bindFunction(this.loadingError.dispatch));
            _loader.addEventListener(IOErrorEvent.IO_ERROR, loaderObject.bindFunction(loaderObject.handleLoaderIOError));
            _loader.addEventListener(SecurityErrorEvent.SECURITY_ERROR, loaderObject.bindFunction(loaderObject.handleLoaderSecurityError));
            _loader.addEventListener(ProgressEvent.PROGRESS, loaderObject.bindFunction(loaderObject.onLoadProgress));
            _loader.addEventListener(Event.COMPLETE, loaderObject.bindFunction(loaderObject.onComplete));
            this._loaderMap.set(loaderName, loaderObject);
            this._queue.push(loaderObject);
            this.startLoading();
            // TODO: @HTML5 - need a new logging system
            //LogChannels.loading.debug("URLRequest added: " + loaderName);
        }
        else {
            // TODO: @HTML5 - need a new logging system
            //LogChannels.loading.fatal("URLRequest-Loader with name: " + loaderName + " already existing");
        }
    }

    public addQueueEndElement(elementName: string, callback: Function = null): void {
        // TODO: @HTML5 - need a new logging system
        //LogChannels.loading.debug("end element added: " + elementName);
        let loaderObject: LoadingQueueEndElement = new LoadingQueueEndElement(elementName, callback);
        loaderObject.loaderObjectFinished.add(this.bindFunction(this.queueElementHasFinished));
        this._queue.push(loaderObject);
        this.startLoading();
    }

    public hasQueEndElementFinished(elementName: string): boolean {
        if (this._finishedQueueEndElements[elementName] != null) {
            return true;
        }
        else {
            return false;
        }
    }

    /**
     * get raw data of a specific subloader (loader must be finished)
     *
     * @param loaderName name of loader
     * @return raw data of finished loader
     */
    public getLoaderData(loaderName: string): Object {
        let loaderObject: LoaderObject = this._loaderMap.get(loaderName);

        if (loaderObject) {
            if (loaderObject.currentState == LoaderObject.STATE_LOADING_PROGRESS_FINISHED) {
                if (loaderObject.loaderInstance instanceof URLLoader) {
                    let urlLoader: URLLoader = (<URLLoader>loaderObject.loaderInstance);
                    return urlLoader.data;
                }
                else if (loaderObject.loaderInstance instanceof Loader) {
                    let loader: Loader = (<Loader>loaderObject.loaderInstance);
                    return loader.contentLoaderInfo.content;
                }
                else if (loaderObject.loaderInstance instanceof createjs.LoadQueue) {
                    let loader: createjs.LoadQueue = (<createjs.LoadQueue>loaderObject.loaderInstance);
                    let listOfLoadedItems = loader.getItems(true);
                    let loadedContent = listOfLoadedItems.length > 0 ? listOfLoadedItems[0]["rawResult"] : null;
                    return loadedContent;
                }
            }
            else {
                // TODO: @HTML5 - need a new logging system
                //LogChannels.loading.fatal("requested loader: " + loaderName + " has not finished loading");
                return null;
            }
        }
        else {
            // TODO: @HTML5 - need a new logging system
            //LogChannels.loading.fatal("requested loader: " + loaderName + " not found");
            return null;
        }

        // TODO: @HTML5 - need a new logging system
        //LogChannels.loading.fatal("unknown error");
        return null;
    }

    public removeLoaderData(loaderName: string): boolean {
        return this._loaderMap.delete(loaderName);
    }

    /**
     * get loader object of a specific subloader
     *
     * @param loaderName name of loader
     * @return LoaderObject
     */
    public getLoaderObject(loaderName: string): LoaderObject {
        let loaderObject: LoaderObject = this._loaderMap.get(loaderName);

        if (loaderObject) {
            return loaderObject;
        }
        else {
            // TODO: @HTML5 - need a new logging system
            //LogChannels.loading.fatal("requested loader: " + loaderName + " not found");
            return null;
        }
    }

    /**
     * checks if subloader has finished loading
     *
     * @param loaderName name of loader
     * @return Boolean flag of loading status
     */
    public hasSubloaderFinished(loaderName: string): boolean {
        let loaderObject: LoaderObject = this._loaderMap.get(loaderName);

        if (loaderObject) {
            return loaderObject.currentState == LoaderObject.STATE_LOADING_PROGRESS_FINISHED;
        }
        else {
            // TODO: @HTML5 - need a new logging system
            //LogChannels.loading.fatal("requested loader: " + loaderName + " not found");
            return false;
        }
    }

    /**
     * get progress of this entire loader as number between 0 and 1
     *
     * @return progress of this loader
     */
    public getCompleteProgress(): number {
        let bytesTotal: number;
        let bytesLoaded: number;
        for (let loaderObject of Array.from(this._loaderMap.values())) {
            bytesTotal += loaderObject.totalBytes;
            bytesLoaded += loaderObject.loadedBytes;
        }
        return bytesLoaded / bytesTotal;
    }

    /**
     * get progress of a specific subloader as number between 0 and 1
     *
     * @return progress of specific subloader
     */
    public getProgressOfSubloader(loaderName: string): number {
        let loaderObject: LoaderObject = this._loaderMap.get(loaderName);

        if (loaderObject) {
            return loaderObject.loadedBytes / loaderObject.totalBytes;
        }
        else {
            // TODO: @HTML5 - need a new logging system
            //LogChannels.loading.fatal("requested loader: " + loaderName + " not found");
            return -1;
        }
    }

    /**
     * get progress of actual loading subloader as number between 0 and 1. if no
     * loader is in queue, return value is -1
     *
     * @return progress of actual subloader.
     */
    public getProgressOfActualSubloader(): number {
        if (this._queue.length > 0) {
            let loaderObject: ILoadingObject = this._queue[0];

            if (loaderObject) {
                if (loaderObject.loadedBytes != 0 || loaderObject.totalBytes != 0) {
                    return loaderObject.loadedBytes / loaderObject.totalBytes;
                }
                else {
                    return 0;
                }
            }
            else {
                // TODO: @HTML5 - need a new logging system
                //LogChannels.loading.fatal("no subloader loading at the moment");
                return -1;
            }
        }
        else {
            return 0;
        }
    }

    /**
     * calculate download data-rate for loaded data till this timestamp
     *
     * @return approx. download data-rate
     */
    public calculateDataRate(): number {
        let loadingTimeSecs: number = (Date.now() - this._loadingStartTime) / 1000;
        let overallLoadedKiloBytes: number = 0;

        for (let loaderObject of Array.from(this._loaderMap.values())) {
            overallLoadedKiloBytes += loaderObject.loadedBytes / 1024;
        }

        return Math.round(overallLoadedKiloBytes / loadingTimeSecs);
    }

    /**
     * checks if GoodgameLoader has an specific subloader
     *
     * @param loaderName name of loader to check
     *
     * @return Boolean if loader is available
     */
    public hasSubloader(loaderName: string): boolean {
        if (this._loaderMap.get(loaderName)) {
            return true;
        }
        else {
            return false;
        }
    }

    /**
     * actual amount of elements in loading queue
     */
    public get elementsInQueue(): number {
        return this._queue.length;
    }

    /**
     * actual amount of elements already loaded
     */
    public get elementsAlreadyLoaded(): number {
        let numTotalElements: number = 0;

        for (let loader of Array.from(this._loaderMap.values())) {
            numTotalElements++;
        }
        return numTotalElements - this._queue.length;

    }

    /**
     * dispose this loader and make it ready for garbage collection
     */
    public dispose(): void {
        this._loadingQueueElementFinished.removeAll();
        this._loadingQueueElementStarted.removeAll();

        for (let key of Array.from(this._loaderMap.keys())) {
            this._loaderMap.get(key).dispose();
            this._loaderMap.delete(key);
        }
        this._loaderMap = null;
    }

    private startLoading(): void {
        if (this._queue.length > 1) {
            this._isLoading = true;
        }
        else {
            this._isLoading = false;
        }

        if (!this._isLoading) {
            if (this._queue.length > 0) {
                if (this.elementsAlreadyLoaded == 0) {
                    this._loadingStartTime = Date.now();
                }
                this._queue[0].startLoadingObject(this.subloaderErrorLogging);
            }
        }
    }

    /**
     * queue element is now starting with loading progress
     */
    private queueElementNowLoading(subloaderName: string): void {
        this._loadingQueueElementStarted.dispatch(this._loaderID, subloaderName);
    }

    private queueElementHasFinished(subloaderName: string): void {

        if ( this._queue.length === 0 || this._queue[0].loaderName !== subloaderName ) {
            // no loader with the name found; most likely the issue that this function is being called twice on failure
            return;
        }
        let finishedLoaderObject: ILoadingObject = this._queue.shift();

        if (finishedLoaderObject instanceof LoadingQueueEndElement) {
            // TODO: @HTML5 - need a new logging system
            //LogChannels.loading.debug("executing queue end element: " + finishedLoaderObject.loaderName);
            finishedLoaderObject.onComplete(null);
            finishedLoaderObject.dispose();
            this._finishedQueueEndElements[finishedLoaderObject.loaderName] = finishedLoaderObject;
            this._isLoading = false;
        }
        let finishedLoaderInstance: EventDispatcher = <EventDispatcher>(finishedLoaderObject.loaderInstance);

        if (finishedLoaderInstance != null) {
            finishedLoaderInstance.removeEventListener(IOErrorEvent.IO_ERROR, finishedLoaderObject.bindFunction(finishedLoaderObject.handleLoaderIOError));
            finishedLoaderInstance.removeEventListener(SecurityErrorEvent.SECURITY_ERROR, finishedLoaderObject.bindFunction(finishedLoaderObject.handleLoaderSecurityError));
            finishedLoaderInstance.removeEventListener(ProgressEvent.PROGRESS, finishedLoaderObject.bindFunction(finishedLoaderObject.onLoadProgress));
            finishedLoaderInstance.removeEventListener(Event.COMPLETE, finishedLoaderObject.bindFunction(finishedLoaderObject.onComplete));
        }

        this._loadingQueueElementFinished.dispatch(this._loaderID, subloaderName);

        // TODO: @HTML5 - need a new logging system
        //LogChannels.loading.debug("queue element has finished: " + finishedLoaderObject.loaderName);

        if (this._queue.length > 0) {
            this._queue[0].startLoadingObject(this.subloaderErrorLogging);
        }
    }
}
